import { type ReactElement, type RefObject } from 'react'
import type React from 'react'
import { useRef, useState, useCallback, useEffect } from 'react'
import * as PropTypes from 'prop-types'
import { styled } from '@mui/system'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { noop } from 'es-toolkit/compat'
import { isNotBlank } from 'common/misc'
import useOnClickOutside from 'hooks/click-outside'
import { defaultShadow, BlankLink } from 'components/foundation'
import colors from 'common/colors'

declare module 'components/popup/popup-internal.js' {
  const PopupInternal: React.FunctionComponent<PopupInternalProps>
  //export default PopupInternal
  export type PixelCoords = {
    x: number
    y: number
  }
  export type PopupInternalProps = {
    onClose: Function
    children: ReactElement<any, any>
    title: string
    pixel: PixelCoords
    ref?: RefObject<HTMLDivElement>
    width?: number
    hideArrow?: boolean
    closeOnOutsideClick?: boolean
    style?: React.CSSProperties
  }
}
export default function PopupInternal(
  props: PopupInternalProps,
): ReactElement<any, any> | null {
  const {
    onClose = noop,
    children,
    title,
    hideArrow = false,
    closeOnOutsideClick = true,
    pixel = {
      x: 0,
      y: 0,
    },
    width = 238,
    style,
  } = props

  const ref = useRef<HTMLDivElement>(null)
  const [withTitle, setWithTitle] = useState(false)

  useOnClickOutside(ref, () => {
    if (closeOnOutsideClick) {
      onClose()
    }
  })

  const onCloseClicked = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    setWithTitle(isNotBlank(title))
  }, [title])

  return (
    <StyledWrapper
      ref={ref}
      $pixel={pixel}
      $withTitle={withTitle}
      $width={width}
      style={style}
    >
      {withTitle ? <StyledHeader key="title">{title}</StyledHeader> : null}
      <StyledClose key="close-link" onClick={onCloseClicked}>
        <FontAwesomeIcon icon="times" color={colors.grey} />
      </StyledClose>
      {children}
      {!hideArrow && <Arrow $pixel={pixel} $width={width} />}
    </StyledWrapper>
  )
}

PopupInternal.propTypes = {
  onClose: PropTypes.func,
  title: PropTypes.string,
  pixel: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  hideArrow: PropTypes.bool,
  closeOnOutsideClick: PropTypes.bool,
  style: PropTypes.object,
}

//@ts-ignore
const StyledHeader = styled('h1')<StyledHeaderProps>(() => ({
  fontSize: 'large',
  marginTop: '9px',
  paddingBottom: '4px',
  borderBottom: `1px solid ${colors.greyLighter}`,
}))

interface StyledWrapperProps {
  $pixel: PixelCoords
  $withTitle?: boolean
  $width?: number
  children: any
  ref: any
}

interface StyledHeaderProps {
  key: string
  children?: string
}

const StyledWrapper = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$pixel' && prop !== '$withTitle' && prop !== '$width',
})<StyledWrapperProps>(({ $pixel, $withTitle = false, $width = 238 }) => ({
  backgroundColor: 'white',
  padding: '8px',
  position: 'fixed',
  top: `${$pixel.y - 20}px`,
  left: `${$pixel.x - 5}px`,
  width: `${$width}px`,
  minHeight: '100px',
  paddingTop: $withTitle ? '0px' : '40px',
  zIndex: 1000,
  overflowY: 'auto',
  ...defaultShadow,
  borderRadius: '4px',
}))

const StyledClose = styled(BlankLink)({
  position: 'absolute',
  right: '12px',
  top: '10px',
})

interface ArrowProps {
  $pixel: PixelCoords
  $width: number
}

const Arrow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$pixel' && prop !== '$width',
})<ArrowProps>(({ $pixel, $width }) => ({
  position: 'fixed',
  top: `${$pixel.y + 38}px`,
  left: `${$pixel.x + $width - 9}px`,
  borderBottom: '10px solid transparent',
  borderTop: '10px solid transparent',
  borderLeft: '10px solid white',
  height: 0,
  width: 0,
}))
